import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, ChevronRight, Check, Plus, User, Users, Key, Calendar, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const AddTeamFlow = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [roles, setRoles] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    roleId: '',
    roleName: '',
    username: '',
    password: '',
    doubleDeckerCertified: false,
    recurringAvailability: [],
    unavailableDates: [],
    schedulerWeight: 3
  });

  const totalSteps = 4;

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles');
      setRoles(response.data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.name.trim()) {
          toast.error('Please enter teamMember name');
          return false;
        }
        return true;
      case 2:
        if (!formData.roleId) {
          toast.error('Please select a role');
          return false;
        }
        return true;
      case 3:
        if (!formData.username.trim() || !formData.password) {
          toast.error('Please enter username and password');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email?.trim() || null,
        phone: formData.phone?.trim() || null,
        username: formData.username.trim(),
        password: formData.password,
        roleId: formData.roleId,
        roleName: formData.roleName,
        recurringAvailability: formData.recurringAvailability,
        unavailableDates: formData.unavailableDates,
        payRate: 20.0,
        schedulerWeight: formData.schedulerWeight,
        doubleDeckerCertified: formData.doubleDeckerCertified
      };

      await api.post('/team', payload);
      toast.success('Team member added successfully! 🎉');
      navigate('/dashboard?tab=team');
    } catch (error) {
      console.error('Error adding teamMember:', error);
      
      let errorMessage = 'Failed to add teamMember';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          const validationErrors = error.response.data.detail.map(err => 
            `${err.loc?.[err.loc.length - 1] || 'Field'}: ${err.msg}`
          ).join(', ');
          errorMessage = `Validation errors: ${validationErrors}`;
        }
      }
      toast.error(errorMessage, { duration: 6000 });
    }
  };

  const generateUsername = () => {
    if (formData.name) {
      const username = formData.name.toLowerCase().replace(/[^a-z0-9]/g, '');
      updateFormData('username', username);
    }
  };

  const addNewRole = () => {
    navigate('/dashboard?tab=roles&action=add');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Basic Information</h2>
              <p className="text-gray-600 mt-2">Let's start with the teamMember's basic details</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-gray-700">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                  placeholder="Enter teamMember's full name"
                  className="mt-1 h-12 text-lg"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  placeholder="team@example.com (optional)"
                  className="mt-1 h-12 text-lg"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Phone Number</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => updateFormData('phone', e.target.value)}
                  placeholder="(555) 123-4567 (optional)"
                  className="mt-1 h-12 text-lg"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Role Assignment</h2>
              <p className="text-gray-600 mt-2">What role will {formData.name || 'this teamMember'} have?</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-4 block">Select Role *</Label>
                <div className="space-y-3">
                  {roles.map((role) => (
                    <div
                      key={role.id}
                      onClick={() => {
                        updateFormData('roleId', role.id);
                        updateFormData('roleName', role.name);
                      }}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.roleId === role.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: role.color || '#3B82F6' }}
                          />
                          <div>
                            <h3 className="font-semibold text-gray-900">{role.name}</h3>
                            {role.description && (
                              <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                            )}
                          </div>
                        </div>
                        {formData.roleId === role.id && (
                          <Check className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* Add New Role Option */}
                  <div
                    onClick={addNewRole}
                    className="p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all"
                  >
                    <div className="flex items-center justify-center gap-2 text-blue-600">
                      <Plus className="w-5 h-5" />
                      <span className="font-medium">Add New Role</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Key className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Login Credentials</h2>
              <p className="text-gray-600 mt-2">Set up login access for {formData.name || 'this teamMember'}</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="username" className="text-sm font-medium text-gray-700">Username *</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => updateFormData('username', e.target.value)}
                    placeholder="username"
                    className="h-12 text-lg"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={generateUsername}
                    className="px-4 h-12"
                  >
                    Auto-generate
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Used to log into the team portal</p>
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => updateFormData('password', e.target.value)}
                  placeholder="Enter secure password"
                  className="mt-1 h-12 text-lg"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters recommended</p>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Review & Confirm</h2>
              <p className="text-gray-600 mt-2">Please review the teamMember details before adding</p>
            </div>

            <Card className="border-2">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-4 border-b">
                    <h3 className="text-lg font-semibold">TeamMember Summary</h3>
                    {formData.roleId && (
                      <Badge 
                        style={{ 
                          backgroundColor: roles.find(r => r.id === formData.roleId)?.color || '#3B82F6',
                          color: 'white'
                        }}
                      >
                        {formData.roleName}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-semibold">{formData.name}</p>
                    </div>
                    
                    {formData.email && (
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-semibold">{formData.email}</p>
                      </div>
                    )}
                    
                    {formData.phone && (
                      <div>
                        <p className="text-sm text-gray-600">Phone</p>
                        <p className="font-semibold">{formData.phone}</p>
                      </div>
                    )}
                    
                    <div>
                      <p className="text-sm text-gray-600">Username</p>
                      <p className="font-semibold">{formData.username}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard?tab=team')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">Add New TeamMember</h1>
            </div>
            
            {/* Progress indicator */}
            <div className="flex items-center gap-2">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full ${
                    i + 1 <= currentStep ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-8 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>

          {currentStep === totalSteps ? (
            <Button
              onClick={handleSubmit}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Add TeamMember
            </Button>
          ) : (
            <Button
              onClick={nextStep}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddTeamFlow;